import React from "react";

/**
 * Enterprise marketing + pricing page (spec §6 public website / §16 redirect
 * target winterdesktops.com/enterprise). Sells Winter Panda as a branded
 * digital gift and sends businesses to the portal (app.winterdesktops.com).
 */
const PORTAL_URL = (
  process.env.REACT_APP_PORTAL_URL || "http://localhost:3100"
).replace(/\/+$/, "");

const PLANS = [
  { name: "Free", price: 0, licences: 3, highlight: false },
  { name: "Starter", price: 50, licences: 25, highlight: false },
  { name: "Professional", price: 100, licences: 100, highlight: true },
  { name: "Business", price: 200, licences: 300, highlight: false },
];

const FEATURES = [
  ["Branded digital gift", "Send Winter Panda with your company name and logo."],
  ["Postcard invitations", "Email recipients a beautiful branded invitation — one click to redeem."],
  ["Steam delivery", "Recipients install and activate via the official Winter Panda Steam page."],
  ["Live tracking", "See sent, redeemed and pending invitations on your dashboard."],
  ["Annual licences", "Simple yearly bundles that auto-renew. Upgrade any time."],
  ["Privacy-first", "The same calm, local-only companion — no monitoring, ever."],
];

export default function EnterprisePage() {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-night-fade font-sans text-frost-100">
      {/* nav */}
      <header className="sticky top-0 z-40 border-b border-white/5 bg-midnight-900/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <a href="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-frost-300/30 to-aurora-violet/20 text-lg ring-1 ring-white/15">
              🐼
            </span>
            <span className="font-display text-lg font-semibold tracking-tight text-frost-50">
              Winter Panda <span className="text-frost-200/50">for Business</span>
            </span>
          </a>
          <a
            href={`${PORTAL_URL}/login`}
            className="rounded-full glass px-5 py-2 text-sm font-semibold text-frost-100 hover:bg-white/10"
          >
            Business sign in
          </a>
        </div>
      </header>

      {/* hero */}
      <section className="relative px-5 pt-20 pb-16 text-center sm:px-8">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 left-1/3 h-72 w-72 rounded-full bg-aurora-cyan/20 blur-[120px]" />
          <div className="absolute top-10 right-1/4 h-80 w-80 rounded-full bg-aurora-violet/20 blur-[130px]" />
        </div>
        <div className="relative mx-auto max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-aurora-cyan">
            Winter Panda for Business
          </span>
          <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.05] tracking-tight text-frost-50 sm:text-6xl">
            Gift a little <span className="text-aurora">winter warmth</span> to your people.
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-frost-200/85">
            Thank clients, colleagues and partners with a branded Winter Panda — a cozy,
            privacy-first desktop companion they’ll actually love. Subscribe, brand it, and send
            invitations in minutes.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={`${PORTAL_URL}/register`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-cozy-300 via-cozy-400 to-cozy-500 px-9 py-4 text-base font-semibold text-midnight-900 shadow-cozy transition-transform hover:-translate-y-0.5"
            >
              Create a business account
            </a>
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 text-sm font-semibold text-frost-100"
            >
              See pricing →
            </a>
          </div>
        </div>
      </section>

      {/* features */}
      <section className="px-5 py-16 sm:px-8">
        <div className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(([title, body]) => (
            <div key={title} className="rounded-3xl glass p-7">
              <h3 className="font-display text-xl font-semibold text-frost-50">{title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-frost-200/75">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* pricing */}
      <section id="pricing" className="px-5 py-16 sm:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-4xl font-semibold tracking-tight text-frost-50 sm:text-5xl">
            Simple annual pricing
          </h2>
          <p className="mt-4 text-lg text-frost-200/80">
            One yearly bundle per organisation. Auto-renews. Upgrade any time.
          </p>
        </div>
        <div className="mx-auto mt-12 grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`relative rounded-[2rem] p-8 shadow-glass ${
                p.highlight
                  ? "glass ring-2 ring-aurora-cyan/40"
                  : "glass"
              }`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-aurora-band bg-[length:200%_200%] px-4 py-1 text-xs font-semibold text-midnight-900 animate-aurora">
                  Most popular
                </span>
              )}
              <h3 className="font-display text-2xl font-semibold text-frost-50">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl font-semibold text-frost-50">
                  {p.price === 0 ? "Free" : `$${p.price}`}
                </span>
                {p.price > 0 && <span className="text-frost-200/60">/year</span>}
              </div>
              <p className="mt-2 text-sm text-frost-200/70">{p.licences} Winter Panda licences</p>
              <a
                href={`${PORTAL_URL}/register`}
                className={`mt-7 block rounded-full px-6 py-3 text-center text-sm font-semibold transition-transform hover:-translate-y-0.5 ${
                  p.highlight
                    ? "bg-gradient-to-br from-cozy-300 via-cozy-400 to-cozy-500 text-midnight-900 shadow-cozy"
                    : "glass text-frost-100 hover:bg-white/10"
                }`}
              >
                {p.price === 0 ? "Start free" : `Get ${p.name}`}
              </a>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-xl text-center text-sm text-frost-200/55">
          Need a complimentary pilot, or pricing for a larger rollout? Email{" "}
          <a href="mailto:support@winterdesktops.com" className="text-aurora-cyan hover:text-frost-50">
            support@winterdesktops.com
          </a>
          .
        </p>
      </section>

      {/* footer */}
      <footer className="border-t border-white/5 bg-white/[0.02]">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 py-10 text-sm text-frost-200/55 sm:flex-row sm:px-8">
          <span>© 2026 Winter Desktops. All rights reserved.</span>
          <div className="flex gap-5">
            <a href="/#/legal/privacy" className="hover:text-frost-50">Privacy</a>
            <a href="/#/legal/terms" className="hover:text-frost-50">Terms</a>
            <a href="/" className="hover:text-frost-50">Winter Panda home</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
